import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  Package, Search, Lock, AlertTriangle, Check, FileText, X,
  IndianRupee, Clock, Filter, Download, Eye, Edit2, Users,
  CheckCircle, XCircle, MessageSquare, Building2, Unlock, Archive,
  Trash2, MoreHorizontal, RefreshCw, ChevronDown, ChevronRight, Grid3X3, List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

/**
 * ADMIN VENDOR RATE MANAGEMENT - PHASE 2A & 2B
 * 
 * Features for Admin/Owner/Staff:
 * - View all vendor rates with explicit AMEND & ARCHIVE actions
 * - Edit rates, descriptions, validity
 * - Approve/Reject/Lock rates
 * - Export approved rates to PDF
 * - Project-Vendor assignment from All Projects view
 * - Vendor comparison for BOQ
 */

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600' },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  locked: { label: 'Locked (BOQ Ready)', color: 'bg-purple-100 text-purple-700' },
  modified_by_admin: { label: 'Modified - Pending Review', color: 'bg-orange-100 text-orange-700' },
  archived: { label: 'Archived', color: 'bg-stone-200 text-stone-500' },
  expired: { label: 'Expired', color: 'bg-stone-100 text-stone-500' },
};

export default function AdminVendorRates() {
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState({ standard_categories: [] });
  const [projects, setProjects] = useState([]);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('rates'); // Control active tab
  
  // Vendor-grouped view
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'vendor-grouped'
  const [expandedVendors, setExpandedVendors] = useState(new Set()); // Which vendors are expanded
  const [selectedRates, setSelectedRates] = useState(new Set()); // Multi-select rates
  
  // Edit panel
  const [editRate, setEditRate] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Status change dialog
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusRate, setStatusRate] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  
  // Archive dialog
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiveRate, setArchiveRate] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  
  // Permanent delete dialog (Admin testing mode)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteRate, setDeleteRate] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Vendor assignment
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignProject, setAssignProject] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  
  // Show archived rates toggle
  const [showArchived, setShowArchived] = useState(false);
  
  // Vendor/Client Preview
  const [showVendorPreview, setShowVendorPreview] = useState(false);
  const [previewVendor, setPreviewVendor] = useState(null);
  const [vendorPreviewData, setVendorPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [ratesRes, categoriesRes, vendorsRes, projectsRes] = await Promise.all([
        axios.get(`${API}/admin/vendor-rates/all`),
        axios.get(`${API}/vendor/categories`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      ]);
      
      setRates(ratesRes.data);
      setCategories(categoriesRes.data);
      setVendors(vendorsRes.data.filter(u => u.role === 'vendor'));
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load vendor rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter rates (excluding archived unless showArchived is true)
  const filteredRates = rates.filter(rate => {
    // Hide archived rates unless explicitly showing them
    if (!showArchived && rate.is_archived) return false;
    // P0 FIX: Check category_id, category_name, or category for filtering
    const rateCategory = rate.category_id || rate.category_name || rate.category;
    if (filterCategory !== 'all' && rateCategory !== filterCategory) return false;
    if (filterVendor !== 'all' && rate.vendor_id !== filterVendor) return false;
    if (filterStatus !== 'all' && rate.status !== filterStatus) return false;
    return true;
  });

  // Open edit panel (Amend)
  const handleEdit = (rate) => {
    setEditRate(rate);
    setEditForm({
      rate: rate.rate,
      unit: rate.unit || 'Sq.ft',
      validity_days: rate.validity_days || 30,
      description: rate.description || '',
      exclusions: rate.exclusions || '',
      gst: rate.gst || 'extra',
      inclusion: rate.inclusion || 'supply_only',
      admin_notes: '',
    });
    setShowEditPanel(true);
  };

  // Archive rate
  const handleArchive = async () => {
    if (!archiveRate || !archiveReason.trim()) {
      toast.error('Please provide a reason for archiving');
      return;
    }
    
    setArchiving(true);
    try {
      await axios.delete(`${API}/admin/vendor-rates/${archiveRate.id}/archive`, {
        params: { reason: archiveReason }
      });
      toast.success('Rate archived successfully. Vendor notified.');
      setShowArchiveDialog(false);
      setArchiveReason('');
      setArchiveRate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive rate');
    } finally {
      setArchiving(false);
    }
  };

  // Unarchive rate
  const handleUnarchive = async (rateId) => {
    try {
      await axios.put(`${API}/admin/vendor-rates/${rateId}/unarchive`);
      toast.success('Rate restored and set to Under Review');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore rate');
    }
  };

  // Preview as Vendor
  const handleVendorPreview = async (vendor) => {
    setPreviewVendor(vendor);
    setLoadingPreview(true);
    setShowVendorPreview(true);
    
    try {
      const response = await axios.get(`${API}/admin/vendor-profile/${vendor.id}`);
      setVendorPreviewData(response.data);
    } catch (error) {
      toast.error('Failed to load vendor preview');
      setShowVendorPreview(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Permanently delete rate (Admin testing mode)
  const handlePermanentDelete = async () => {
    if (!deleteRate || !deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/vendor-rates/${deleteRate.id}/permanent`, {
        params: { reason: deleteReason }
      });
      toast.success('Rate permanently deleted.');
      setShowDeleteDialog(false);
      setDeleteReason('');
      setDeleteRate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rate');
    } finally {
      setDeleting(false);
    }
  };

  // Save edit
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/vendor-rates/${editRate.id}/edit`, null, {
        params: {
          rate: editForm.rate,
          unit: editForm.unit,
          validity_days: editForm.validity_days,
          description: editForm.description,
          exclusions: editForm.exclusions,
          gst: editForm.gst,
          inclusion: editForm.inclusion,
          admin_notes: editForm.admin_notes,
        }
      });
      toast.success('Rate modified. Vendor notified for review.');
      setShowEditPanel(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rate');
    } finally {
      setSaving(false);
    }
  };

  // Change status
  const handleStatusChange = async () => {
    try {
      await axios.put(`${API}/admin/vendor-rates/${statusRate.id}/status`, null, {
        params: { status: newStatus, reason: statusReason }
      });
      toast.success(`Rate status updated to ${newStatus}`);
      setShowStatusDialog(false);
      setStatusReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Export vendor rates PDF
  const handleExportPDF = async (vendorId) => {
    try {
      const response = await axios.get(
        `${API}/admin/vendor-rates/export/pdf/${vendorId}?status=approved`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendor_rates_${vendorId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Assign vendors to project
  const handleAssignVendors = async () => {
    try {
      await axios.post(`${API}/projects/${assignProject.id}/vendors/assign`, selectedVendors);
      toast.success(`${selectedVendors.length} vendor(s) assigned to project`);
      setShowAssignDialog(false);
      setSelectedVendors([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign vendors');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-stone-400">Loading vendor rates...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-vendor-rates">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              Vendor Rate Management
            </h1>
            <p className="text-stone-500">Review, approve, and manage all vendor submitted rates.</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-stone-800">{rates.length}</p>
            <p className="text-sm text-stone-500">Total Rates</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-amber-600">
              {rates.filter(r => r.status === 'submitted' || r.status === 'under_review').length}
            </p>
            <p className="text-sm text-stone-500">Pending Review</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-green-600">
              {rates.filter(r => r.status === 'approved' || r.status === 'locked').length}
            </p>
            <p className="text-sm text-stone-500">Approved/Locked</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-purple-600">
              {rates.filter(r => r.status === 'locked').length}
            </p>
            <p className="text-sm text-stone-500">BOQ Ready</p>
          </div>
          <div className="apple-card p-4">
            <p className="text-2xl font-bold text-stone-600">{vendors.length}</p>
            <p className="text-sm text-stone-500">Active Vendors</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="rates">All Rates</TabsTrigger>
            <TabsTrigger value="vendors">Vendors</TabsTrigger>
            <TabsTrigger value="assignments">Project Assignments</TabsTrigger>
          </TabsList>

          {/* All Rates Tab */}
          <TabsContent value="rates" className="mt-6">
            {/* Filters */}
            <div className="apple-card p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-stone-500" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>
                
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.standard_categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterVendor} onValueChange={setFilterVendor}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vendors</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Show Archived Toggle */}
                <label className="flex items-center gap-2 cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-stone-600">Show Archived</span>
                </label>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-600">View:</span>
                <Button 
                  variant={viewMode === 'table' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setViewMode('table')}
                >
                  <List size={14} className="mr-1" /> Table
                </Button>
                <Button 
                  variant={viewMode === 'vendor-grouped' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setViewMode('vendor-grouped')}
                >
                  <Users size={14} className="mr-1" /> By Vendor
                </Button>
              </div>
              {selectedRates.size > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-purple-600 font-medium">{selectedRates.size} item(s) selected</span>
                  <div className="flex gap-1 border-l border-stone-200 pl-2 ml-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/vendor-rates/bulk-approve`, {
                            rate_ids: Array.from(selectedRates)
                          });
                          toast.success(`${selectedRates.size} rate(s) approved`);
                          setSelectedRates(new Set());
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to approve rates');
                        }
                      }}
                    >
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/vendor-rates/bulk-lock`, {
                            rate_ids: Array.from(selectedRates)
                          });
                          toast.success(`${selectedRates.size} rate(s) locked (BOQ Ready)`);
                          setSelectedRates(new Set());
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to lock rates');
                        }
                      }}
                    >
                      <Lock size={14} className="mr-1" /> BOQ Ready
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      onClick={async () => {
                        const reason = prompt('Enter rejection reason:');
                        if (reason === null) return;
                        try {
                          await axios.post(`${API}/admin/vendor-rates/bulk-reject`, {
                            rate_ids: Array.from(selectedRates),
                            reason
                          });
                          toast.success(`${selectedRates.size} rate(s) rejected`);
                          setSelectedRates(new Set());
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to reject rates');
                        }
                      }}
                    >
                      <XCircle size={14} className="mr-1" /> Reject
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-gray-600 border-gray-200 hover:bg-gray-50"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/admin/vendor-rates/bulk-archive`, {
                            rate_ids: Array.from(selectedRates)
                          });
                          toast.success(`${selectedRates.size} rate(s) archived`);
                          setSelectedRates(new Set());
                          fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to archive rates');
                        }
                      }}
                    >
                      <Archive size={14} className="mr-1" /> Archive
                    </Button>
                    {user.role === 'owner' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={async () => {
                          if (!window.confirm(`Permanently delete ${selectedRates.size} rate(s)? This cannot be undone.`)) return;
                          try {
                            await axios.post(`${API}/admin/vendor-rates/bulk-delete`, {
                              rate_ids: Array.from(selectedRates)
                            });
                            toast.success(`${selectedRates.size} rate(s) permanently deleted`);
                            setSelectedRates(new Set());
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to delete rates');
                          }
                        }}
                      >
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedRates(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* Vendor-Grouped View */}
            {viewMode === 'vendor-grouped' && (
              <div className="space-y-4">
                {(() => {
                  // Group filtered rates by vendor
                  const vendorGrouped = {};
                  filteredRates.forEach(rate => {
                    const vendorId = rate.vendor_id || 'unknown';
                    const vendorName = rate.vendor_info?.name || rate.vendor_name || 'Unknown Vendor';
                    if (!vendorGrouped[vendorId]) {
                      vendorGrouped[vendorId] = { name: vendorName, email: rate.vendor_info?.email, rates: [] };
                    }
                    vendorGrouped[vendorId].rates.push(rate);
                  });

                  return Object.entries(vendorGrouped).map(([vendorId, vendorData]) => {
                    const isExpanded = expandedVendors.has(vendorId);
                    const vendorRateIds = new Set(vendorData.rates.map(r => r.id));
                    const allSelected = vendorData.rates.every(r => selectedRates.has(r.id));
                    const someSelected = vendorData.rates.some(r => selectedRates.has(r.id));

                    return (
                      <div key={vendorId} className="apple-card overflow-hidden">
                        {/* Vendor Header - Collapsible */}
                        <div 
                          className="flex items-center justify-between p-4 bg-stone-50 border-b border-stone-200 cursor-pointer hover:bg-stone-100"
                          onClick={() => {
                            const newExpanded = new Set(expandedVendors);
                            if (isExpanded) {
                              newExpanded.delete(vendorId);
                            } else {
                              newExpanded.add(vendorId);
                            }
                            setExpandedVendors(newExpanded);
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                            <div>
                              <h3 className="font-semibold text-stone-800">{vendorData.name}</h3>
                              {vendorData.email && <p className="text-xs text-stone-500">{vendorData.email}</p>}
                            </div>
                            <Badge variant="outline" className="ml-2">{vendorData.rates.length} rates</Badge>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Select All for this vendor */}
                            <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = someSelected && !allSelected;
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const newSelected = new Set(selectedRates);
                                  if (e.target.checked) {
                                    vendorData.rates.forEach(r => newSelected.add(r.id));
                                  } else {
                                    vendorData.rates.forEach(r => newSelected.delete(r.id));
                                  }
                                  setSelectedRates(newSelected);
                                }}
                                className="w-4 h-4 rounded accent-purple-600"
                              />
                              <span className="text-sm text-stone-600">Select All</span>
                            </label>
                          </div>
                        </div>

                        {/* Expanded Rates Table */}
                        {isExpanded && (
                          <table className="w-full">
                            <thead className="bg-white border-b border-stone-100">
                              <tr>
                                <th className="w-10 px-2 py-2"></th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-stone-500">Item</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-stone-500">Category</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-stone-500">Rate</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-stone-500">Unit</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-stone-500">Status</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-stone-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vendorData.rates.map(rate => {
                                const statusConfig = STATUS_CONFIG[rate.status] || STATUS_CONFIG.draft;
                                const isSelected = selectedRates.has(rate.id);
                                return (
                                  <tr key={rate.id} className={`border-b border-stone-50 hover:bg-stone-50 ${isSelected ? 'bg-purple-50' : ''}`}>
                                    <td className="px-2 py-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const newSelected = new Set(selectedRates);
                                          if (e.target.checked) {
                                            newSelected.add(rate.id);
                                          } else {
                                            newSelected.delete(rate.id);
                                          }
                                          setSelectedRates(newSelected);
                                        }}
                                        className="w-4 h-4 rounded accent-purple-600"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <p className="font-medium text-sm text-stone-800">{rate.material_name || rate.item_name}</p>
                                      <p className="text-xs text-stone-500 truncate max-w-[180px]">{rate.description}</p>
                                    </td>
                                    <td className="px-3 py-2 text-sm">{rate.category_name || rate.category || 'N/A'}</td>
                                    <td className="px-3 py-2 text-right font-bold">₹{rate.rate?.toLocaleString()}</td>
                                    <td className="px-3 py-2 text-sm">{rate.unit}</td>
                                    <td className="px-3 py-2">
                                      <Badge variant="outline" className={`text-xs ${statusConfig.color}`}>
                                        {statusConfig.label}
                                      </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <Button variant="ghost" size="sm" onClick={() => handleEdit(rate)}>
                                        <Edit2 size={14} />
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}

            {/* Original Table View */}
            {viewMode === 'table' && (
            <div className="apple-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Item</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Vendor</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Category</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-stone-600">Rate</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Unit</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Validity</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRates.map((rate) => {
                    const statusConfig = STATUS_CONFIG[rate.status] || STATUS_CONFIG.draft;
                    const validityEnd = new Date(rate.validity_end);
                    const now = new Date();
                    const daysRemaining = Math.max(0, Math.ceil((validityEnd - now) / (1000 * 60 * 60 * 24)));
                    const isExpired = validityEnd <= now;
                    
                    return (
                      <tr key={rate.id} className={`border-b border-stone-100 hover:bg-stone-50 ${rate.is_archived ? 'bg-stone-50 opacity-60' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-stone-800">{rate.material_name}</p>
                          <p className="text-xs text-stone-500 truncate max-w-[200px]">{rate.description}</p>
                          {rate.original_values && rate.status === 'modified_by_admin' && (
                            <p className="text-xs text-orange-600 mt-1">
                              Original: ₹{rate.original_values?.rate?.toLocaleString()}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{rate.vendor_info?.name || rate.vendor_name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">
                            {/* P0 FIX: Use category_name directly, fallback to lookup or category_id */}
                            {rate.category_name || categories.standard_categories?.find(c => c.id === (rate.category_id || rate.category))?.name || rate.category_id || rate.category || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-bold text-stone-800">₹{rate.rate?.toLocaleString()}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">{rate.unit}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${isExpired ? 'text-red-600' : daysRemaining <= 7 ? 'text-amber-600' : ''}`}>
                            {isExpired ? 'Expired' : `${daysRemaining}d`}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* EXPLICIT AMEND BUTTON */}
                            {!rate.is_archived && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEdit(rate)}
                                className="text-blue-600 hover:bg-blue-50"
                                title="Amend Rate"
                                data-testid={`amend-rate-${rate.id}`}
                              >
                                <Edit2 size={14} className="mr-1" />
                                Amend
                              </Button>
                            )}
                            
                            {/* DROPDOWN FOR MORE ACTIONS */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {/* Status Changes */}
                                {!rate.is_archived && (
                                  <>
                                    <DropdownMenuItem onClick={() => {
                                      setStatusRate(rate);
                                      setNewStatus('approved');
                                      setShowStatusDialog(true);
                                    }}>
                                      <CheckCircle size={14} className="mr-2 text-green-600" />
                                      Approve
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => {
                                      setStatusRate(rate);
                                      setNewStatus('locked');
                                      setShowStatusDialog(true);
                                    }}>
                                      <Lock size={14} className="mr-2 text-purple-600" />
                                      Lock (BOQ Ready)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => {
                                      setStatusRate(rate);
                                      setNewStatus('rejected');
                                      setShowStatusDialog(true);
                                    }}>
                                      <XCircle size={14} className="mr-2 text-red-600" />
                                      Reject
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                
                                {/* ARCHIVE / UNARCHIVE */}
                                {rate.is_archived ? (
                                  <DropdownMenuItem onClick={() => handleUnarchive(rate.id)}>
                                    <RefreshCw size={14} className="mr-2 text-green-600" />
                                    Restore Rate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setArchiveRate(rate);
                                      setShowArchiveDialog(true);
                                    }}
                                    className="text-amber-600"
                                  >
                                    <Archive size={14} className="mr-2" />
                                    Archive
                                  </DropdownMenuItem>
                                )}
                                
                                {/* PERMANENT DELETE (Admin only - Testing Mode) */}
                                {user.role === 'owner' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setDeleteRate(rate);
                                        setShowDeleteDialog(true);
                                      }}
                                      className="text-red-600"
                                    >
                                      <Trash2 size={14} className="mr-2" />
                                      Permanently Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredRates.length === 0 && (
                <div className="text-center py-12 text-stone-500">
                  <Package className="mx-auto mb-4" size={40} />
                  <p>No rates found matching your filters.</p>
                </div>
              )}
            </div>
            )}
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Sort vendors by rate count (those with rates first) */}
              {[...vendors].sort((a, b) => {
                const aRates = rates.filter(r => r.vendor_id === a.id && !r.is_archived).length;
                const bRates = rates.filter(r => r.vendor_id === b.id && !r.is_archived).length;
                return bRates - aRates;
              }).map((vendor) => {
                const vendorRates = rates.filter(r => r.vendor_id === vendor.id && !r.is_archived);
                const approvedCount = vendorRates.filter(r => r.status === 'approved' || r.status === 'locked').length;
                const hasRates = vendorRates.length > 0;
                
                return (
                  <div key={vendor.id} className={`apple-card p-4 ${!hasRates ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{vendor.name}</h3>
                        <p className="text-sm text-stone-500">{vendor.email}</p>
                      </div>
                      <Badge variant="outline" className={hasRates ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-500"}>
                        {hasRates ? 'Active' : 'No Rates'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-center p-3 bg-stone-50 rounded-lg mb-3">
                      <div>
                        <p className="text-lg font-bold">{vendorRates.length}</p>
                        <p className="text-xs text-stone-500">Total</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{approvedCount}</p>
                        <p className="text-xs text-stone-500">Approved</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-purple-600">
                          {vendorRates.filter(r => r.status === 'locked').length}
                        </p>
                        <p className="text-xs text-stone-500">BOQ Ready</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {hasRates && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => {
                            setFilterVendor(vendor.id);
                            setActiveTab('rates'); // Switch to rates tab
                          }}
                        >
                          <Eye size={14} className="mr-1" />
                          View Rates
                        </Button>
                      )}
                      {!hasRates && (
                        <span className="text-sm text-stone-400 flex-1 text-center py-2">No rates submitted</span>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-purple-600 hover:bg-purple-50"
                        onClick={() => handleVendorPreview(vendor)}
                        data-testid={`preview-vendor-${vendor.id}`}
                      >
                        <Users size={14} className="mr-1" />
                        Preview
                      </Button>
                      {approvedCount > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleExportPDF(vendor.id)}
                        >
                          <Download size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Project Assignments Tab */}
          <TabsContent value="assignments" className="mt-6">
            <div className="space-y-4">
              {projects.map((project) => {
                const assignedVendors = vendors.filter(v => 
                  (project.assigned_vendors || []).includes(v.id)
                );
                
                return (
                  <div key={project.id} className="apple-card p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{project.name}</h3>
                        <p className="text-sm text-stone-500">{project.location}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setAssignProject(project);
                          setSelectedVendors(project.assigned_vendors || []);
                          setShowAssignDialog(true);
                        }}
                      >
                        <Users size={14} className="mr-1" />
                        Manage Vendors
                      </Button>
                    </div>
                    
                    {assignedVendors.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {assignedVendors.map(v => (
                          <Badge key={v.id} variant="secondary">
                            {v.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-stone-500">No vendors assigned</p>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Rate Side Panel */}
      <Sheet open={showEditPanel} onOpenChange={setShowEditPanel}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Edit Vendor Rate</SheetTitle>
            <SheetDescription className="text-stone-500">
              Modify this rate. Vendor will be notified for review.
            </SheetDescription>
          </SheetHeader>

          {editRate && (
            <div className="space-y-4 mt-6">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium">{editRate.material_name}</p>
                <p className="text-sm text-stone-500">Vendor: {editRate.vendor_info?.name || editRate.vendor_name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rate (₹)</Label>
                  <Input
                    type="number"
                    value={editForm.rate}
                    onChange={(e) => setEditForm({...editForm, rate: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={editForm.unit} onValueChange={(v) => setEditForm({...editForm, unit: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sq.ft">Sq.ft</SelectItem>
                      <SelectItem value="Rft">Rft</SelectItem>
                      <SelectItem value="Lft">Lft</SelectItem>
                      <SelectItem value="No.s">No.s</SelectItem>
                      <SelectItem value="Set">Set</SelectItem>
                      <SelectItem value="Pair">Pair</SelectItem>
                      <SelectItem value="Kg">Kg</SelectItem>
                      <SelectItem value="Ltr">Ltr</SelectItem>
                      <SelectItem value="Mtr">Mtr</SelectItem>
                      <SelectItem value="Sqm">Sqm</SelectItem>
                      <SelectItem value="Cum">Cum</SelectItem>
                      <SelectItem value="Lot">Lot</SelectItem>
                      <SelectItem value="Point">Point</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Validity (days from now)</Label>
                <Select value={editForm.validity_days?.toString()} onValueChange={(v) => setEditForm({...editForm, validity_days: parseInt(v)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="45">45 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="120">120 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">365 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description / Specification</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={2}
                />
              </div>

              <div>
                <Label>Exclusions</Label>
                <Input
                  value={editForm.exclusions}
                  onChange={(e) => setEditForm({...editForm, exclusions: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>GST</Label>
                  <Select value={editForm.gst} onValueChange={(v) => setEditForm({...editForm, gst: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="included">Included</SelectItem>
                      <SelectItem value="extra">Extra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Inclusion</Label>
                  <Select value={editForm.inclusion} onValueChange={(v) => setEditForm({...editForm, inclusion: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supply_only">Supply Only</SelectItem>
                      <SelectItem value="labour_only">Labour Only</SelectItem>
                      <SelectItem value="supply_labour">Supply + Labour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Admin Notes (visible to vendor)</Label>
                <Textarea
                  value={editForm.admin_notes}
                  onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                  placeholder="Explain the changes..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditPanel(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={saving} className="flex-1">
                  {saving ? 'Saving...' : 'Save & Notify Vendor'}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Rate Status</DialogTitle>
            <DialogDescription>
              Update the status of this vendor rate.
            </DialogDescription>
          </DialogHeader>

          {statusRate && (
            <div className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-medium">{statusRate.material_name}</p>
                <p className="text-sm text-stone-500">Current: {STATUS_CONFIG[statusRate.status]?.label}</p>
              </div>

              <div>
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="locked">Locked (BOQ Ready)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newStatus === 'rejected' && (
                <div>
                  <Label>Rejection Reason</Label>
                  <Textarea
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusChange}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Vendors to Project</DialogTitle>
            <DialogDescription>
              {assignProject?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {vendors.map((vendor) => (
              <label 
                key={vendor.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedVendors.includes(vendor.id) 
                    ? 'bg-blue-50 border-blue-300' 
                    : 'bg-white border-stone-200 hover:bg-stone-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedVendors.includes(vendor.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedVendors([...selectedVendors, vendor.id]);
                    } else {
                      setSelectedVendors(selectedVendors.filter(id => id !== vendor.id));
                    }
                  }}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium">{vendor.name}</p>
                  <p className="text-sm text-stone-500">{vendor.email}</p>
                </div>
              </label>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignVendors}>
              Assign {selectedVendors.length} Vendor(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Rate Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Archive className="text-amber-500" size={20} />
              Archive Vendor Rate
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the rate for <strong>{archiveRate?.material_name}</strong> from vendor <strong>{archiveRate?.vendor_info?.name || archiveRate?.vendor_name}</strong>.
              <br /><br />
              Archived rates are:
              <ul className="list-disc ml-6 mt-2">
                <li>Hidden from BOQ and comparison tools</li>
                <li>Retained in audit history</li>
                <li>Can be restored later if needed</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label>Reason for archiving *</Label>
            <Textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Enter reason for archiving this rate..."
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setArchiveRate(null);
              setArchiveReason('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleArchive}
              disabled={archiving || !archiveReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {archiving ? 'Archiving...' : 'Archive Rate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Dialog (Owner Only - Testing Mode) */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Permanently Delete Rate
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-red-600">WARNING: This action cannot be undone!</strong>
              <br /><br />
              You are about to permanently delete the rate for <strong>{deleteRate?.material_name}</strong> from vendor <strong>{deleteRate?.vendor_info?.name || deleteRate?.vendor_name}</strong>.
              <br /><br />
              This will:
              <ul className="list-disc ml-6 mt-2 text-red-600">
                <li>Remove the rate from all records</li>
                <li>Remove from audit history</li>
                <li>Cannot be recovered</li>
              </ul>
              <br />
              Use this only for testing/cleanup purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label>Reason for permanent deletion *</Label>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter reason for permanently deleting this rate..."
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteRate(null);
              setDeleteReason('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePermanentDelete}
              disabled={deleting || !deleteReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Vendor Preview Sheet */}
      <Sheet open={showVendorPreview} onOpenChange={setShowVendorPreview}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Users size={20} className="text-purple-600" />
              Preview as Vendor
            </SheetTitle>
            <SheetDescription className="text-stone-500">
              Viewing exactly what {previewVendor?.name || 'vendor'} sees in their profile
            </SheetDescription>
          </SheetHeader>

          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : vendorPreviewData ? (
            <div className="mt-6 space-y-6">
              {/* Vendor Info */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="font-semibold text-purple-900">{vendorPreviewData.vendor?.name}</h3>
                <p className="text-sm text-purple-700">{vendorPreviewData.vendor?.email}</p>
                <p className="text-sm text-purple-600">{vendorPreviewData.vendor?.phone}</p>
              </div>

              {/* Assigned Projects */}
              <div>
                <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Building2 size={16} />
                  Assigned Projects ({vendorPreviewData.assigned_projects?.length || 0})
                </h4>
                {vendorPreviewData.assigned_projects?.length > 0 ? (
                  <div className="space-y-2">
                    {vendorPreviewData.assigned_projects.map(project => (
                      <div key={project.id} className="p-3 bg-stone-50 rounded-lg">
                        <p className="font-medium">{project.name}</p>
                        <p className="text-sm text-stone-500">{project.location}</p>
                        <Badge variant="outline" className="mt-1">{project.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500 italic">No projects assigned</p>
                )}
              </div>

              {/* Rates Summary */}
              <div>
                <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Package size={16} />
                  Rates Summary
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 bg-stone-50 rounded-lg text-center">
                    <p className="text-lg font-bold">{vendorPreviewData.rates_summary?.total || 0}</p>
                    <p className="text-xs text-stone-500">Total</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-green-600">{vendorPreviewData.rates_summary?.approved || 0}</p>
                    <p className="text-xs text-green-600">Approved</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-orange-600">{vendorPreviewData.rates_summary?.pending_review || 0}</p>
                    <p className="text-xs text-orange-600">Needs Review</p>
                  </div>
                </div>
              </div>

              {/* Recent Rates */}
              <div>
                <h4 className="font-medium text-stone-700 mb-2">Recent Rates</h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {vendorPreviewData.rates?.slice(0, 10).map(rate => (
                    <div key={rate.id} className="p-3 bg-stone-50 rounded-lg flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{rate.material_name}</p>
                        <p className="text-xs text-stone-500">₹{rate.rate?.toLocaleString()} / {rate.unit}</p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={STATUS_CONFIG[rate.status]?.color || 'bg-stone-100'}
                      >
                        {STATUS_CONFIG[rate.status]?.label || rate.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Notifications */}
              {vendorPreviewData.recent_notifications?.length > 0 && (
                <div>
                  <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                    <MessageSquare size={16} />
                    Recent Notifications
                  </h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {vendorPreviewData.recent_notifications.slice(0, 5).map(notif => (
                      <div key={notif.id} className={`p-3 rounded-lg ${notif.read ? 'bg-stone-50' : 'bg-blue-50 border border-blue-200'}`}>
                        <p className="font-medium text-sm">{notif.title}</p>
                        <p className="text-xs text-stone-500">{notif.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
